import { useTranslation } from 'react-i18next';
import { Navigate } from 'react-router-dom';

import {
    Heading2,
    HorizontalDivider,
    Stack,
    useDisableBackButton,
} from '@myphx-engineering/phx-design-system';
import type { HeadlinePriceType } from '@phx/data-sources/medication-marketplace';

import { getFragment } from '../../../graphql/client/helpers/get-fragment';
import { type DigitallyReceivedPrescriberOrderInfoFragment } from '../../../graphql/generated/graphql';
import { useDrugKey } from '../../../hooks/use-drug-key';
import { useFlags } from '../../../hooks/use-flags';
import { usePriceCardContent } from '../../../hooks/use-price-card-content-props';
import type { PriceCardV2Flag } from '../../../types/non-boolean-flags';
import { getAbsoluteRoute } from '../../../util/get-absolute-route';
import { Prescriber } from '../../common/Prescriber';
import { ProviderInfo } from '../../provider/ProviderInfo';
import { PricingCardV2 } from '../fill/common/PricingCardV2';

import { OrderConfirmationHeader } from './shared/OrderConfirmationHeader';
import { OrderPrescriptionDetails } from './shared/OrderPrescriptionDetails';
import { OrderPricing } from './shared/OrderPricing';
import { OrderSavingsCard } from './shared/OrderSavingsCard';
import { OuraPromotionalModule } from './shared/OuraPromotionalModule';
import { TheresMoreCard } from './shared/TheresMoreCard';

export type OrderConfirmationV2Props = {
    prescriberOrder: DigitallyReceivedPrescriberOrderInfoFragment;
};

const OrderConfirmationV2 = ({ prescriberOrder }: OrderConfirmationV2Props) => {
    const { t } = useTranslation('lilly');
    const { enablePriceCardV2 } = useFlags();
    const prescriber = getFragment(prescriberOrder.prescriber);
    const provider = getFragment(prescriberOrder.assignedPharmacyProvider);
    const medication = getFragment(prescriberOrder?.authorizedMedication);
    const selectedOffer = getFragment(prescriberOrder?.latestSelectedOffer);

    const drugKey = useDrugKey(medication);
    const priceCardFlag = enablePriceCardV2 as PriceCardV2Flag;

    const cardContentOverride = usePriceCardContent({
        medication,
        headlinePriceType: selectedOffer?.headlinePriceType as
            | HeadlinePriceType
            | undefined
            | null,
    });

    const showV2PriceCard =
        !!drugKey &&
        priceCardFlag.drugKeys.includes(drugKey) &&
        cardContentOverride?.pricingCard;

    useDisableBackButton(true);

    if (!medication || !provider) {
        return <Navigate to={getAbsoluteRoute('error')} />;
    }

    return (
        <Stack data-testid="order-confirmation-page-root">
            <OrderConfirmationHeader
                prescriber={prescriber}
                prescriberOrder={prescriberOrder}
                provider={provider}
            />

            <HorizontalDivider />

            <Stack gap="sm">
                <Heading2>{t('orderConfirmation.pharmacy.header')}</Heading2>
                <ProviderInfo provider={provider} />
            </Stack>

            <OrderSavingsCard
                medication={medication}
                selectedOffer={selectedOffer}
            />
            {showV2PriceCard ? (
                <PricingCardV2
                    pricingCard={cardContentOverride.pricingCard!}
                    priceNote={cardContentOverride.priceNote}
                />
            ) : (
                <OrderPricing
                    medication={medication}
                    selectedOffer={selectedOffer}
                />
            )}

            <OrderPrescriptionDetails prescriberOrder={prescriberOrder} />

            <Prescriber prescriber={prescriber} />

            <OuraPromotionalModule prescriberOrder={prescriberOrder} />

            <TheresMoreCard />
        </Stack>
    );
};

export default OrderConfirmationV2;
